import { useCallback, useMemo, useRef, useState } from 'react';

import { getUrlSearchParamsEntries } from '@order/utils';

import type { getAppNavigationContainerRef } from '../../navigation';
import { featureFlagOverrides } from './overrides';
import type { FeatureFlagOverrides } from './types';

/**
 * Returns the feature flag overrides with the correct priority.
 *
 * 1. Query overrides (?features.<feature-name>=<feature-value>).
 * 2. Local overrides (apps/order/src/context/LaunchDarkly/overrides.json).
 */
export const useOverrides = () => {
  const { queryOverrides, removeQueryOverrides } = useQueryOverrides();
  const { localOverrides } = useLocalOverrides();

  const overrides = useMemo(
    () => ({ ...localOverrides, ...queryOverrides }),
    [localOverrides, queryOverrides],
  );

  return { overrides, removeQueryOverrides };
};

const useLocalOverrides = () => {
  const [localOverrides, setLocalOverrides] = useState(featureFlagOverrides);

  return { localOverrides, setLocalOverrides };
};

const useQueryOverrides = () => {
  const { current: queryOverrides } = useRef<FeatureFlagOverrides>(
    parseParametersFromQuery(getUrlSearchParamsEntries())
      .featureFlagsOverridesParams,
  );

  /**
   * Removes feature flags overrides params from route's params (setting them to `undefined`).
   */
  const removeQueryOverrides = useCallback(
    (
      navigationContainerRef: ReturnType<typeof getAppNavigationContainerRef>,
    ) => {
      const navigation = navigationContainerRef?.current;
      const route = navigation?.getCurrentRoute?.();
      const allParams = (route?.params ?? {}) as Record<string, string>;
      const { routeParamsWithoutOverrides } =
        parseParametersFromQuery(allParams);

      navigation?.setParams?.({ ...allParams, ...routeParamsWithoutOverrides });
    },
    [],
  );

  return {
    queryOverrides,
    removeQueryOverrides,
  };
};

// Receives all parameters from navigation,
// Returns only the route params and only the feature params.
const parseParametersFromQuery = (params: Record<string, string>) => {
  // Selects all parameters that starts with "features.".
  const featureFlagsFromParams = Object.keys(params).filter((key) =>
    key.startsWith('features.'),
  );

  // Maps query params into feature flags.
  // From: { features.<feature-name>: '<feature-value>' }
  // To: { feature-name: feature-value }
  const featureFlagsOverridesParams = Object.fromEntries(
    featureFlagsFromParams.map((key) => [
      key.split('features.')[1],
      mapParamToFeatureValue(params[key as never]),
    ]),
  );

  // Sets all params that contain "features." in route params to `undefined`.
  const routeParamsWithoutOverrides = Object.fromEntries(
    featureFlagsFromParams.map((key) => [key, undefined]),
  );

  return {
    featureFlagsOverridesParams,
    routeParamsWithoutOverrides,
  };
};

// The parameters are received as "true", "false", "something".
// This converts "true" to true and "false" to false, while keeping "something".
const mapParamToFeatureValue = (param: string): boolean | string => {
  if (param === 'true') return true;

  if (param === 'false') return false;

  return param;
};
